import { computeTotalLifetimeCost, formatCurrency } from '../utils/lifecarePlanUtils';

const PLAN_VALUE_RANGE = 0.1;

const PLACEHOLDER_DIAGNOSIS =
    'Patient presents with injuries consistent with the reported mechanism of loss, including persistent cervical, thoracic, and lumbar complaints supported by the records reviewed.';

const PLACEHOLDER_LIFE_EXPECTANCY =
    'Life expectancy is consistent with normal actuarial tables for the patient age, adjusted for documented conditions.';

const PLACEHOLDER_SUMMARY_OF_RECORDS =
    'Records reviewed reflect ongoing treatment, diagnostic imaging, and conservative care with limited sustained improvement.';

const PLACEHOLDER_SUBSECTIONS = [
    {
        title: 'Physician & Specialist Care',
        content: 'Ongoing evaluation and oversight by treating specialists.',
        tables: [
            { item: 'Specialist follow-up', beginningAge: 40, beginningYear: 2026, endingAge: 60, endingYear: 2046, isEndingLifeExpectancy: true, frequencyPerYear: 2, frequencyComment: '2x / year', unitCost: 350, lifetimeCost: 14000 },
            { item: 'Diagnostic imaging', beginningAge: 40, beginningYear: 2026, endingAge: 50, endingYear: 2036, isEndingLifeExpectancy: false, frequencyPerYear: 1, frequencyComment: 'As needed', unitCost: 1200, lifetimeCost: 12000 },
        ],
    },
    {
        title: 'Rehabilitation & Therapy',
        content: 'Functional restoration and pain management support.',
        tables: [
            { item: 'Physical therapy', beginningAge: 40, beginningYear: 2026, endingAge: 45, endingYear: 2031, isEndingLifeExpectancy: false, frequencyPerYear: 52, frequencyComment: '1x / week', unitCost: 150, lifetimeCost: 39000 },
        ],
    },
];

const buildPlanMeta = (info) => {
    const parts = [];
    if (info?.currentAge) parts.push(`Age ${info.currentAge}`);
    if (info?.dateOfIncident) parts.push(`Date of Incident: ${info.dateOfIncident}`);
    return parts.join('  |  ');
};

const useLifecarePlanDocument = ({ plan }) => {
    const info = plan?.info;
    const planSubsections = plan?.futureCareNeeds?.subsections || [];
    const subsections = planSubsections.length > 0 ? planSubsections : PLACEHOLDER_SUBSECTIONS;

    const totalLifetimeCost = computeTotalLifetimeCost({ futureCareNeeds: { subsections } });
    const totalLow = totalLifetimeCost * (1 - PLAN_VALUE_RANGE);
    const totalHigh = totalLifetimeCost * (1 + PLAN_VALUE_RANGE);

    return {
        info,
        planMeta: buildPlanMeta(info),
        diagnosisTitle: plan?.diagnosis?.title || 'Diagnosis',
        diagnosisBody: plan?.diagnosis?.content || PLACEHOLDER_DIAGNOSIS,
        lifeExpectancyTitle: plan?.lifeExpectancy?.title || 'Life Expectancy',
        lifeExpectancyBody: plan?.lifeExpectancy?.content || PLACEHOLDER_LIFE_EXPECTANCY,
        lifeExpectancyYears: Number(plan?.lifeExpectancy?.lifeExpectancy) || 0,
        summaryTitle: plan?.summaryOfRecords?.title || 'Summary of Records Reviewed',
        summaryBody: plan?.summaryOfRecords?.content || PLACEHOLDER_SUMMARY_OF_RECORDS,
        futureCareTitle: plan?.futureCareNeeds?.title || 'Future Care Needs',
        subsections,
        hasPlanValue: totalLifetimeCost > 0,
        totalValueFormatted: formatCurrency(totalLifetimeCost),
        totalValueLowFormatted: formatCurrency(totalLow),
        totalValueHighFormatted: formatCurrency(totalHigh),
    };
};

export default useLifecarePlanDocument;
